<template>
  <help-dialog title="Domain Model" maxWidth="800px">
    <template #default>
      <h3 class="mb-2 text-black">
        <v-icon>mdi-alpha-d-box</v-icon>&nbsp;Domain Model Overview
      </h3>
      <p class="text-grey-darken-3">
        The Domain Model is a key artifact in Domain-Driven Design (DDD) and is used
        to create a shared understanding between domain experts and developers.
        Examples of when to use the Domain Model include:
      </p>
      <br/>
      <ul class="pl-10">
        <li class="text-grey-darken-3">API design</li>
        <li class="text-grey-darken-3">System modernization or microservice design</li>
        <li class="text-grey-darken-3">Breaking down monolithic systems</li>
        <li class="text-grey-darken-3">Code generation</li>
      </ul>
      <br />
      <p class="text-grey-darken-4">
        For a full walkthrough of how to create a Domain Model with Continuum Design, please
        <a
          href="https://www.qlerify.com/domain-driven-design-tool"
          target="_blank"
          class="text-blue-accent-4"
        >
          click here
        </a>.
      </p>
      <br/><br/>
      <!-- Commands are grouped by aggregate. You can also see the
      command parameters and the associated entities. You can further group entities in Bounded
      Contexts and even generate the code code skeleton for a Bounded Context (a Service API). -->

      <h3 class="mb-2 text-black">
        <v-icon icon="mdi-cog"></v-icon>&nbsp;Configuration
      </h3>
      <p class="text-grey-darken-3">
        First, make sure to have one card type set as ‘Command’,
        one set as ‘Aggregate Root’ and one set as 'Read Model' in workflow settings (the
        <v-icon size="x-small">mdi-cog</v-icon> icon top right).
      </p>

      <br/><br/>

      <h3 class="mb-2 text-black">
        Add Cards to Events
      </h3>
      <p class="text-grey-darken-3">
        Now make sure to add one <b>Command</b> and one <b>Aggregate Root</b> to each <b>Event</b>
        (by adding cards of the corresponding types).
      </p>
      <br/>
      <ul class="pl-10">
        <li class="text-grey-darken-3"><b>Note:</b> Add only <b>one Command</b> and <b>one Root Entity</b> per Event. Additional cards
          will be ignored.
        </li>
        <li class="text-grey-darken-3">
          <b>Hint:</b> When modeling, consider each Event as the result of one Command invoked on one Aggregate.
          If an Event, for example <b>Created order</b>, actually is the result of two separate Commands,
          such as <b>1. Created customer</b> and <b>2. Created Order</b>, then split up this step into two steps.
        </li>
      </ul>

      <br/><br/>

      <h3 class="mb-2 text-black">
        View Grouped Commands
      </h3>
      <p class="text-grey-darken-3">
        Now you can view Commands and Events grouped by their Aggregate Roots.
        The names and descriptions can be updated directly in the diagram.
        Commands and events belong together if they are positioned
        on the same level horizontally.
      </p>

      <br/><br/>

      <h3 class="mb-2 text-black">
        Data Fields
      </h3>
      <p class="text-grey-darken-3">
        <b>Commands</b> and <b>Read Models</b>, along with <b>Entities</b>, can also contain data fields or attributes. These attributes can be linked to related entities.
      </p>
      <br/>
      <p class="text-grey-darken-3">
        If you prefer a more compact view—for example, to better visualize <b>Bounded Contexts</b>—you can hide attributes by deselecting the <b>Show data fields</b> checkbox.
      </p>
      <br/>
      <p class="text-grey-darken-3">
        To view data fields under an <b>Aggregate Root</b>, you must first select an <b>Aggregate Root Entity</b> by clicking the yellow box and choosing an entity (you may need to create entities before you can select one).
      </p>
      <br/>

      <p class="text-grey-darken-3">
        After selecting an entity as the <b>Aggregate Root</b>, you can edit it using the pen icon at the bottom left of the yellow box. The following actions are available for each field on the <b>Entity</b>:
      </p>

      <ul class="ml-5">
        <li>
          <b>Select entity:</b>&nbsp;
          Establish a relationship between a field and another entity. This action is available
          only for fields with the <b>object</b> data type.
        </li>
        <li><b>Change data type:</b>&nbsp;Set the field's data type (string, number, boolean, or object).
          For object fields, select a related entity to indicate a relationship.
        </li>
        <li>
          <b>Set as required/optional:</b>&nbsp;Toggles whether the field is required or not.
        </li>
        <li><b>Delete:</b>&nbsp;Removes the field from the list.</li>
        <li><b>1:1 or 1:N</b>&nbsp;Sets the cardinality of the relationship (one-to-one or one-to-many).</li>
      </ul>
      <br />

      <p class="text-grey-darken-3">
        The following actions are available for each field on the <b>Command</b>:
      </p>
      <ul class="ml-5">
        <li>
          <b>Select entity:</b>&nbsp;
          Establish a relationship between a field and an <b>Entity</b>. This usually mirrors a relationship
          on the <b>Entity</b> that is set as the <b>Aggregate Root</b>.
        </li>
        <li>
          <b>Show/Hide in form:</b>&nbsp;
          Sets whether the field will be shown in the UI form mockup under the sidebar's <b>Data Fields</b> tab.
        </li>
        <li>
          <b>Set as required/optional:</b>&nbsp;Toggles whether the field is required or not.
        </li>
        <li><b>Delete:</b>&nbsp;Removes the field from the list.</li>
        <li><b>1:1 or 1:N</b>&nbsp;Sets the cardinality of the relationship (one-to-one or one-to-many).</li>
      </ul>
      <br />

      <p class="text-grey-darken-3">
        The following actions are available for each field on the <b>Read Model</b>:
      </p>
      <ul class="ml-5">
        <li>
          <b>Mark as Filter / Unmark Filter:</b>&nbsp;
          Marks the field as a filter / query parameter used to narrow the query.
        </li>
        <li>
          <b>Mark as Computed / Unmark Computed:</b>&nbsp;
          Marks the field as calculated at runtime (e.g. totals, counts, derived values). Computed fields do not need to exist on the queried entity.
        </li>
        <li>
          <b>Select entity:</b>&nbsp;
          Establish a relationship between the field and an <b>Entity</b>.
        </li>
        <li><b>Delete:</b>&nbsp;Removes the field from the list.</li>
        <li><b>1:1 or 1:N</b>&nbsp;Sets the cardinality of the relationship (one-to-one or one-to-many).</li>
      </ul>
      <br />
      A Read Model can be set to <b>List</b> or <b>Single Item</b>, depending on whether it should return multiple items or just one.
      <br /><br />

      <h3 class="mb-2 text-black">
        Select Fields
      </h3>
      <p class="text-grey-darken-3">
        For both <b>Commands</b> and <b>Read Models</b>, you can use the <b>Select fields</b> action to automatically populate
        them with fields from the <b>Aggregate Root Entity</b>. This creates fields that correspond to the entity's attributes,
        making it easier to define what data the command will accept or what data the read model will return.
      </p>

      <br /><br />

      <h3 class="mb-2 text-black">
        Bounded Contexts
      </h3>
      <p class="text-grey-darken-3">
        The next step is to assign one or several <b>Entities</b> to one or many <b>Bounded contexts</b>. This is done by clicking
        the button <b>Select bounded context</b> on the entity. Related entities can belong to the same bounded
        context as the <b>Aggregate Root</b> or to another bounded context. If a related entity belongs to another bounded
        context, a label with the text <b>Integration with other bounded contexts</b> indicates that we have
        an integration point.
      </p>

      <br/><br/>

      <h3 class="mb-2 text-black">
        Recommendations for Code Generation
      </h3>
      <p class="text-grey-darken-3">
        To get the best results from AI code generation, follow these recommendations when constructing your Domain Model:
      </p>
      <br/>
      <ul class="pl-10">
        <li class="text-grey-darken-3">
          <b>Use camelCase for data fields:</b> Name data fields of commands, read models and entities using camelCase
          convention (e.g., <code>productType</code>, <code>customerName</code>) for consistent code generation.
        </li>
        <li class="text-grey-darken-3">
          <b>Resolve all alerts:</b> Before generating code, address all yellow and red alert indicators in your domain model.
          These alerts highlight missing configurations or validation issues that could affect code quality.
        </li>
        <li class="text-grey-darken-3">
          <b>Select entities for all components:</b> Ensure that an entity is selected for both <b>Read Models</b> and
          <b>Aggregate Roots</b>. This ensures proper relationships and data structure in the generated code.
        </li>
        <li class="text-grey-darken-3">
          <b>Verify Read Model cardinality:</b> Check that each Read Model is correctly configured as either <b>List</b>
          (returns multiple items) or <b>Single Item</b> (returns one item by ID). This determines the correct API endpoint structure.
        </li>
        <li class="text-grey-darken-3">
          <b>Review Given-When-Then scenarios:</b> Verify that all GWT test scenarios are relevant and make sense for the
          associated commands. Well-defined scenarios result in better test coverage and more accurate business logic.
        </li>
      </ul>

      <br/><br/>

      <h3 class="mb-2 text-black">
        Service API / Code Generation
      </h3>
      <p class="text-grey-darken-3">
        Continuum Design's AI-powered code generation transforms your Domain Model into production-ready code
        through a guided, seven-step workflow:
      </p>
      <br/>
      <ul class="pl-10">
        <li class="text-grey-darken-3">
          <b>Step 1: Validate Domain Model</b> - Check your Domain Model for missing configurations, unlinked entities,
          and other issues. Resolve all errors and warnings before generating code to ensure the best results.
        </li>
        <li class="text-grey-darken-3">
          <b>Step 2: Framework Selection</b> - Select the JavaScript/Express starter template with TypeScript/React
          frontend. This provides a complete project structure foundation with a backend server, React frontend, and
          Jest testing framework. Additional templates (NestJS, FastAPI, Spring Boot, ASP.NET, Fiber, etc.)
          are being added continuously.
        </li>
        <li class="text-grey-darken-3">
          <b>Step 3: API Specification Review</b> - Review the automatically generated OpenAPI/Swagger specification
          from your Domain Model. This specification defines all your API endpoints before code generation begins.
        </li>
        <li class="text-grey-darken-3">
          <b>Step 4: Code Generation</b> - Click <b>Generate API</b> to create your codebase. Choose to include
          backend (server folder with commands, entities, and queries), frontend (client folder with React application),
          or both. The system intelligently updates only the parts of your codebase affected by domain changes.
        </li>
        <li class="text-grey-darken-3">
          <b>Step 5: Save Changes</b> - Review AI-generated code in a diff view with side-by-side comparisons.
          Approve changes for individual files using the <b>Apply patch</b> option, or click
          <b>Approve all changes</b> to accept all suggestions at once.
        </li>
        <li class="text-grey-darken-3">
          <b>Step 6: Run Tests</b> - Execute your test suite to verify that the generated code works correctly.
          The system runs Jest tests for your backend implementation.
        </li>
        <li class="text-grey-darken-3">
          <b>Step 7: Commit and Push to GitHub</b> - Navigate to the GitHub tab to commit your changes directly with custom
          commit messages, or download the complete codebase as a ZIP file.
        </li>
      </ul>
      <br/>
      <p class="text-grey-darken-3">
        Additional features include selecting specific language models (LLMs) for code generation, resetting
        the code view to start fresh, and per-item generation to target individual commands, entities, or read models.
      </p>
      <br/>
      <p class="text-grey-darken-4">
        For a full walkthrough of Code Generation,
        <a
          href="https://www.qlerify.com/ai-generated-code"
          target="_blank"
          class="text-blue-accent-4"
        >
          click here
        </a>.
      </p>
    </template>
  </help-dialog>
</template>

<script setup>
import HelpDialog from '@/components/HelpDialog.vue'
</script>
